import { toDate } from "@repo/utils/date";
import { parseDocs } from "@utils/functions/Delivery";
import { useMemo } from "react";
import type { FindDeliveryByIdQuery } from "@/libs/generated/operations";

type DeliveryData = FindDeliveryByIdQuery["findDeliveryById"];
type AddressData = NonNullable<DeliveryData["fromAddress"]>;

import { FORM_DEFAULTS } from "../form-config";
import type { AddressValues, DeliveryFormValues } from "../types";
import { ADDRESS_DEFAULTS, VEHICLE_DEFAULTS } from "../types";

// ─── Transform delivery API data → form values ─────────────────────────────

function toFormAddress(addr: AddressData | null | undefined): AddressValues {
  if (!addr) return { ...ADDRESS_DEFAULTS };
  return {
    ...ADDRESS_DEFAULTS,
    latitude: addr.latitude,
    longitude: addr.longitude,
    city: addr.city,
    address: addr.address,
    postalCode: addr.postalCode,
    place_id: addr.place_id ?? "",
    country: addr.country ?? "",
    addressPrecision: addr.addressPrecision ?? "",
  };
}

function transformDeliveryToFormData(delivery: DeliveryData): DeliveryFormValues {
  /** Form fields hold instants as UTC ISO strings; "" when unset. */
  const formatDate = (d: string | Date | null | undefined): string =>
    toDate(d)?.toISOString() ?? "";
  const vehicle = delivery.vehicles?.find((item) => !item.toRetrieve);
  const returnVehicle = delivery.vehicles?.find((item) => item.toRetrieve);

  return {
    step0: {
      clientFirstName: delivery.clientFirstName ?? "",
      clientLastName: delivery.clientLastName ?? "",
      clientEmail: delivery.clientEmail ?? "",
      clientPhone: delivery.clientPhone ?? "",
      clientCompany: delivery.clientCompany ?? "",
      clientSiret: delivery.clientSiret ?? "",
      clientDrivingLicenceNumber: delivery.clientDrivingLicenceNumber ?? "",
      selectedMandataireId: delivery.clientId ? String(delivery.clientId) : "",
      selectedOrganizationId: delivery.organization?.slug ?? "",
      representativeFirstName: delivery.representativeFirstName ?? "",
      representativeLastName: delivery.representativeLastName ?? "",
      representativeEmail: delivery.representativeEmail ?? "",
      representativePhone: delivery.representativePhone ?? "",
      representativeCompany: delivery.representativeCompany ?? "",
      representativeSiret: delivery.representativeSiret ?? "",
      carAvailabilityDate: formatDate(delivery.carAvailabilityDate),
      carPickUpDate: formatDate(delivery.carPickUpDate),
      carDeliveryDeadline: formatDate(delivery.carDeliveryDeadline),
    },
    step1: {
      fromAddress: toFormAddress(delivery.fromAddress),
      toAddress: toFormAddress(delivery.toAddress),
      vehicle: vehicle
        ? {
            id: vehicle.id,
            carType: vehicle.carType ?? "",
            type: vehicle.type ?? "",
            vin: vehicle.vin ?? "",
            vehicleCode: vehicle.vehicleCode ?? "",
            marque: vehicle.marque ?? "",
            model: vehicle.model ?? "",
            toRetrieve: false,
          }
        : { ...VEHICLE_DEFAULTS },
    },
    step2: {
      toReturnFromAddress: toFormAddress(delivery.toReturnFromAddress),
      toReturnToAddress: toFormAddress(delivery.toReturnToAddress),
      returnVehicle: returnVehicle
        ? {
            id: returnVehicle.id,
            carType: returnVehicle.carType ?? "",
            type: returnVehicle.type ?? "",
            vin: returnVehicle.vin ?? "",
            vehicleCode: returnVehicle.vehicleCode ?? "",
            marque: returnVehicle.marque ?? "",
            model: returnVehicle.model ?? "",
            toRetrieve: true,
          }
        : { ...VEHICLE_DEFAULTS },
    },
    lastStep: {
      category: delivery.category ?? (delivery.type === "OKM" ? "OKM" : "STANDARD"),
      selectedOptions: {
        selected: (delivery.selectedOptions ?? []).map((opt) => ({
          id: String(opt?.optionId ?? opt?.id ?? ""),
          name: String(opt?.name ?? ""),
          price: Number(opt?.clientPrice ?? opt?.clientPrice ?? 0),
          ...(opt?.files ? { file: String(opt.files) } : {}),
        })),
      },
      comment: delivery.comment ?? "",
      docs: parseDocs(delivery.docs),
    },
  };
}

// ─── Hook ───────────────────────────────────────────────────────────────────

export function useDeliveryFormDefaults(data: DeliveryData | undefined, storageKey: string) {
  const initialValues = useMemo((): DeliveryFormValues => {
    // Edit mode — use delivery data
    if (data) {
      sessionStorage.removeItem(storageKey);
      return transformDeliveryToFormData(data);
    }

    // New form — try session storage
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (raw) {
        return JSON.parse(raw) as DeliveryFormValues;
      }
    } catch {
      // Ignore parse errors
    }

    // Fallback
    return FORM_DEFAULTS;
  }, [data, storageKey]);

  const clearStorage = () => sessionStorage.removeItem(storageKey);

  const saveToStorage = (values: DeliveryFormValues) => {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(values));
    } catch {
      // Ignore storage errors
    }
  };

  return { initialValues, clearStorage, saveToStorage } as const;
}
